import Header from '../../../components/layout/header.component';
import Asaid from '../../../components/layout/asaid.component';
import Footer from '../../../components/layout/footer.component';
import { EmailEditor, EditorRef } from 'react-email-editor';
import { useEffect, useRef, useState } from 'react';
import { useSession } from '../../../hooks/use-session.hook';
import Navigate from '../../../components/cards/header-navigate.component';
import Button from '../../../components/forms/button.component';
import { Controller, useForm } from 'react-hook-form';
import Select from 'react-select';
import Input from '../../../components/forms/input.component';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import { CompaniesService } from '../../../services/companies.service';
import { AlertService } from '../../../services/alert.service';
import { AccountsService } from '../../../services/accounts.service';
import { TemplatesService } from '../../../services/templates.service';
import { PROFILES } from '../../../constants';
import { useLocation, useNavigate } from 'react-router-dom';
import Icon from '../../../components/forms/icon.component';
import useBgPages from '../../../hooks/use-bg-pages.hook';
import { useTranslation } from 'react-i18next';

export default function MailingBuilderPage() {
  useBgPages();
  const { t } = useTranslation();
  const emailEditorRef = useRef<EditorRef>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const { session, selectedAccount } = useSession();
  const [companies, setCompanies] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  const defaultValues = {
    company: '',
    account: '',
    name: '',
    html: '',
    design: ''
  };

  const {
    register,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { errors, isSubmitting, isValid }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const { company } = watch();

  const submit = async (data: typeof defaultValues) => {
    try {
      const { html, design } = await exportHtml();
      const dataToSend: any = {
        account: data.account,
        name: data.name,
        html,
        design
      };

      const confirmed = await AlertService.question(
        t('general.atention'),
        t('templates.mailing.confirmations.save-message'),
        () => {}
      );

      if (!confirmed) return;

      if (isEditMode) {
        const template = location.state?.template;
        await TemplatesService.editTemplateMailing({
          _id: template._id,
          ...dataToSend,
          status: template.status ?? true
        });
        await AlertService.toastSuccess(t('templates.mailing.messages.update-success'));
      } else {
        await TemplatesService.addTemplateMailing(dataToSend);
        await AlertService.toastSuccess(t('templates.mailing.messages.save-success'));
      }

      navigate('/templates-panel/templates-mailing');
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const exportHtml = async (): Promise<{ html: string; design: any }> => {
    return new Promise((resolve) => {
      emailEditorRef?.current?.editor?.exportHtml((data) => {
        const { html, design } = data;

        resolve({ html, design });
      });
    });
  };

  const onLoad = () => {
    console.log('Email editor loaded');
  };

  const getCompanies = async () => {
    try {
      const data = await CompaniesService.getCompanies();
      const companiesOptions = data.map((item: any) => ({
        value: item._id,
        label: item.name,
        type: item.service_type
      }));
      setCompanies(companiesOptions);

      if (isEditMode && location.state?.template && isInitialLoad) {
        const template = location.state.template;
        const companyOption = companiesOptions.find(
          (comp: any) => comp.label === template.account.company.name
        );

        if (companyOption) {
          setValue('company', companyOption.value);
          setValue('name', template.name, { shouldValidate: true, shouldDirty: true });

          await loadAccountsAndSetTemplate(companyOption.value, template);
        }
        setIsInitialLoad(false);
      }
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setCompanies([]);
    }
  };

  const loadAccountsAndSetTemplate = async (companyID: string, template: any) => {
    try {
      const data = await AccountsService.getAccountsForCompany(companyID);
      const accountsOptions = data.map((item: any) => ({
        value: item._id,
        label: item.name,
        type: item.service_type
      }));
      setAccounts(accountsOptions);

      const accountOption = accountsOptions.find((acc: any) => acc.label === template.account.name);

      setTimeout(() => {
        if (accountOption) {
          setValue('account', accountOption.value, {
            shouldValidate: true,
            shouldDirty: true
          });
        }
      }, 100);

      if (template.design && emailEditorRef.current?.editor) {
        setTimeout(() => {
          emailEditorRef.current?.editor?.loadDesign(template.design);
        }, 500);
      }
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setAccounts([]);
    }
  };

  const handleEnterFullscreen = () => {
    const wrapper = document.getElementById('email-editor-wrapper');
    if (!wrapper) return;

    if (!document.fullscreenElement) {
      wrapper.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const getAccounts = async (companyID: string) => {
    try {
      const data = await AccountsService.getAccountsForCompany(companyID);
      const accountsOptions = data.map((item: any) => ({
        value: item._id,
        label: item.name,
        type: item.service_type
      }));
      setAccounts(accountsOptions);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
      setAccounts([]);
    }
  };

  useEffect(() => {
    if (company && (!isEditMode || !isInitialLoad)) {
      setValue('account', '');
      getAccounts(company);
    }
  }, [company, setValue, isInitialLoad]);

  useEffect(() => {
    if (location.state?.template && accounts.length > 0 && isInitialLoad) {
      const template = location.state.template;
      const accountOption = accounts.find((acc) => acc.label === template.account.name);

      if (accountOption) {
        setValue('account', accountOption.value);
      }
    }
  }, [accounts, setValue, isInitialLoad, location.state]);

  useEffect(() => {
    getCompanies();
  }, []);

  useEffect(() => {
    if (session.profile === PROFILES.ADMIN) {
      setValue('account', selectedAccount.account_id, {
        shouldValidate: true,
        shouldDirty: true
      });
    }
  }, [selectedAccount, session]);

  const isEditMode = !!location.state?.template;

  return (
    <>
      <div className="page d-flex flex-column h-100">
        <Asaid />
        <div className="wrapper d-flex flex-column flex-grow-1 h-100" id="kt_wrapper">
          <Header />
          <div className="flex-grow-1 d-flex flex-column" style={{ overflow: 'hidden' }}>
            <div className="p-8 content border-raduis-top">
              <div className="row">
                <Navigate
                  btnBack
                  title={
                    isEditMode
                      ? t('templates.mailing.builder.edit-title')
                      : t('templates.mailing.builder.create-title')
                  }
                  titleBolder={t('templates.mailing.title-bold')}
                  subtitle={
                    isEditMode
                      ? t('templates.mailing.builder.edit-subtitle')
                      : t('templates.mailing.builder.create-subtitle')
                  }
                  breadcrumbItems={
                    isEditMode
                      ? t('templates.mailing.breadcrumb-edit', { returnObjects: true })
                      : t('templates.mailing.breadcrumb-builder', { returnObjects: true })
                  }
                />
              </div>
              <div className="card mb-0 p-3">
                <form onSubmit={handleSubmit((data) => submit(data))}>
                  <div className="flex-column current">
                    <div className="pb-0">
                      <div className="row">
                        {session.profile === PROFILES.ROOT && (
                          <>
                            <div className="col-md-3 mb-5">
                              <div className="fv-row mb-7">
                                <label className="form-label required">
                                  {t('templates.mailing.builder.company-label')}
                                </label>
                                <Controller
                                  control={control}
                                  name="company"
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error }
                                  }) => (
                                    <>
                                      <Select
                                        options={companies}
                                        noOptionsMessage={({ inputValue }) =>
                                          inputValue
                                            ? `Sin coincidencias ${inputValue}`
                                            : 'Sin opciones'
                                        }
                                        className={
                                          error
                                            ? 'form-select-custom is-invalid'
                                            : 'form-select-custom'
                                        }
                                        classNamePrefix="form-select-custom"
                                        placeholder={t(
                                          'templates.mailing.builder.company-placeholder'
                                        )}
                                        onChange={(selectedOption) => {
                                          onChange(selectedOption?.value);
                                        }}
                                        value={
                                          companies.find((option: any) => option.value === value) ||
                                          null
                                        }
                                      />
                                      <InvalidFeedback error={error} />
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                            <div className="col-md-3 mb-5">
                              <div className="fv-row mb-7">
                                <label className="form-label required">
                                  {t('templates.mailing.builder.account-label')}
                                </label>
                                <Controller
                                  control={control}
                                  name="account"
                                  rules={{ required: true }}
                                  render={({
                                    field: { onChange, value },
                                    fieldState: { error }
                                  }) => (
                                    <>
                                      <Select
                                        options={accounts}
                                        noOptionsMessage={({ inputValue }) =>
                                          inputValue
                                            ? `Sin coincidencias ${inputValue}`
                                            : 'Sin opciones'
                                        }
                                        className={
                                          error
                                            ? 'form-select-custom is-invalid'
                                            : 'form-select-custom'
                                        }
                                        classNamePrefix="form-select-custom"
                                        placeholder={t(
                                          'templates.mailing.builder.account-placeholder'
                                        )}
                                        onChange={(selectedOption) => {
                                          onChange(selectedOption?.value);
                                        }}
                                        value={
                                          accounts.find((option: any) => option.value === value) ||
                                          null
                                        }
                                      />
                                      <InvalidFeedback error={error} />
                                    </>
                                  )}
                                />
                              </div>
                            </div>
                          </>
                        )}
                        <div className="col-md-3 mb-5">
                          <div className="fv-row mb-0">
                            <Input
                              label={t('templates.mailing.builder.name-label')}
                              requiredIndicator="required"
                              placeholder={t('templates.mailing.builder.name-placeholder')}
                              errors={errors?.name}
                              {...register('name', {
                                required: true,
                                setValueAs: (value) => value.trim()
                              })}
                            />
                          </div>
                        </div>
                        <div className="col-md-3 mb-5 mt-10">
                          <div className="fv-row mb-0">
                            <Button
                              type="submit"
                              typeStepperBtn="submit"
                              id="sendData"
                              className="btn btn-lg btn-primary hover-scale"
                              disabled={!isValid}
                              isLoading={isSubmitting}
                            >
                              {location.state?.template ? t('general.update') : t('general.save')}

                              <Icon icon="ki-send" size="fs-1" className="ms-2" />
                            </Button>
                            <Button
                              type="button"
                              className="btn btn-light-info ms-5"
                              onClick={handleEnterFullscreen}
                            >
                              <Icon icon="ki-arrow-two-diagonals" size="fs-2x" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
            <div
              id="email-editor-wrapper"
              className={`mail-editor-builder bg-light border-raduis-bottom p-5`}
            >
              <EmailEditor
                ref={emailEditorRef}
                onLoad={onLoad}
                style={{ height: '100%', width: '100%' }}
                options={{
                  projectId: 275025,
                  displayMode: 'email',
                  locale: t('languages.selected'),
                  tools: { menu: { enabled: false } }
                }}
              />
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
